import { ethers } from 'ethers';
import CELTokenService from './CELTokenService';
import InnovationUnitsService from './InnovationUnitsService';
import ProjectStakingService from './ProjectStakingService';
import EmissionControllerService from './EmissionControllerService';
import ProtocolTreasuryService from './ProtocolTreasuryService';
import AdminService from './AdminService';

// ABIs
import CELTokenABI from '../../abis/CELToken.json';
import InnovationUnitsABI from '../../abis/InnovationUnits.json';
import ProjectStakingABI from '../../abis/ProjectStaking.json';
import EmissionControllerABI from '../../abis/EmissionController.json';
import ProtocolTreasuryABI from '../../abis/ProtocolTreasury.json';

// Network configurations
import config from '../../config/contracts';

/**
 * Factory for creating contract services
 */
class ContractServiceFactory {
  /**
   * Initialize the factory with a provider
   * @param {Object} provider - Ethers.js provider
   * @param {string} networkId - Network ID (optimismSepolia, optimism, localhost)
   */
  constructor(provider, networkId = 'optimismSepolia', signer = null,isConnected = false) {
    this.provider = provider;
    this.signer = signer || provider; // use wallet signer for writes, else provider for reads
    console.log("Provider is:", this.provider?.constructor?.name);
    console.log("Signer is:", this.signer?.constructor?.name);
    this.isConnected = isConnected;
        

    
    console.log(`ContractServiceFactory: Initializing with networkId "${networkId}"`);
    console.log(`ContractServiceFactory: Available networks in config: ${Object.keys(config).join(', ')}`);
    
    // Normalize network ID to lowercase with no underscores
    this.networkId = networkId.toLowerCase().replace(/_/g, '');
    
    // Try to get signer, fallback to provider for read-only operations
    try {
      if (isConnected) {
        this.signer = provider.getSigner();
      }
    } catch (error) {
      console.log('No signer available, operating in read-only mode');
      this.signer = provider;
    }
    
    this.contracts = {};
    this.services = {};
    
    // Get network configuration - handle both formats
    this.config = null;
    
    // Try different possible formats
    if (config[this.networkId]) {
      console.log(`ContractServiceFactory: Found exact match for "${this.networkId}"`);
      this.config = config[this.networkId];
    } else if (config['optimismSepolia'] && 
              (this.networkId === 'optimismsepolia' || this.networkId === 'optimism_sepolia')) {
      console.log(`ContractServiceFactory: Using "optimismSepolia" config for "${this.networkId}"`);
      this.config = config['optimismSepolia'];
      this.networkId = 'optimismSepolia'; // Normalize to the format in config
    } else if (config['optimism'] && 
              (this.networkId === 'optimism')) {
      console.log(`ContractServiceFactory: Using "optimism" config for "${this.networkId}"`);
      this.config = config['optimism'];
    } else if (config['localhost'] && 
              (this.networkId === 'localhost')) {
      console.log(`ContractServiceFactory: Using "localhost" config for "${this.networkId}"`);
      this.config = config['localhost'];
    }
    
    if (!this.config) {
      console.error(`No configuration found for network ${networkId}. Available networks: ${Object.keys(config).join(', ')}`);
      throw new Error(`No configuration found for network ${networkId}`);
    }
  }

  /**
   * Get CEL Token contract instance
   * @returns {Object} - Ethers.js contract instance
   */
  getCELTokenContract() {
    if (!this.contracts.celToken) {
      try {
        this.contracts.celToken = new ethers.Contract(
          this.config.celToken,
          CELTokenABI,
          this.signer
        );
      } catch (error) {
        console.error('Error creating CEL Token contract:', error);
        throw error;
      }
    }
    return this.contracts.celToken;
  }

  /**
   * Get Innovation Units contract instance
   * @returns {Object} - Ethers.js contract instance
   */
  getInnovationUnitsContract() {
    if (!this.contracts.innovationUnits) {
      try {
        this.contracts.innovationUnits = new ethers.Contract(
          this.config.innovationUnits,
          InnovationUnitsABI,
          this.signer
        );
      } catch (error) {
        console.error('Error creating Innovation Units contract:', error);
        throw error;
      }
    }
    return this.contracts.innovationUnits;
  }

  /**
   * Get Project Staking contract instance
   * @returns {Object} - Ethers.js contract instance
   */
  getProjectStakingContract() {
    if (!this.contracts.projectStaking) {
      try {
        this.contracts.projectStaking = new ethers.Contract(
          this.config.projectStaking,
          ProjectStakingABI,
          this.signer
        );
      } catch (error) {
        console.error('Error creating Project Staking contract:', error);
        throw error;
      }
    }
    return this.contracts.projectStaking;
  }

  /**
   * Get Emission Controller contract instance
   * @returns {Object} - Ethers.js contract instance
   */
  getEmissionControllerContract() {
    if (!this.contracts.emissionController) {
      try {
        this.contracts.emissionController = new ethers.Contract(
          this.config.emissionController,
          EmissionControllerABI,
          this.signer
        );
      } catch (error) {
        console.error('Error creating Emission Controller contract:', error);
        throw error;
      }
    }
    return this.contracts.emissionController;
  }

  /**
   * Get Protocol Treasury contract instance
   * @returns {Object} - Ethers.js contract instance
   */
  getProtocolTreasuryContract() {
    if (!this.contracts.protocolTreasury) {
      try {
        this.contracts.protocolTreasury = new ethers.Contract(
          this.config.protocolTreasury,
          ProtocolTreasuryABI,
          this.signer
        );
      } catch (error) {
        console.error('Error creating Protocol Treasury contract:', error);
        throw error;
      }
    }
    return this.contracts.protocolTreasury;
  }

  /**
   * Get CEL Token service
   * @returns {CELTokenService} - CEL Token service instance
   */
  getCELTokenService() {
    if (!this.services.celToken) {
      const contract = this.getCELTokenContract();
      this.services.celToken = new CELTokenService(contract);
    }
    return this.services.celToken;
  }

  /**
   * Get Innovation Units service
   * @returns {InnovationUnitsService} - Innovation Units service instance
   */
  getInnovationUnitsService() {
    if (!this.services.innovationUnits) {
      const contract = this.getInnovationUnitsContract();
      const celTokenContract = this.getCELTokenContract();
      this.services.innovationUnits = new InnovationUnitsService(contract, celTokenContract);
    }
    return this.services.innovationUnits;
  }

  /**
   * Get Project Staking service
   * @returns {ProjectStakingService} - Project Staking service instance
   */
  getProjectStakingService() {
    if (!this.services.projectStaking) {
      const contract = this.getProjectStakingContract();
      const celTokenContract = this.getCELTokenContract();
      this.services.projectStaking = new ProjectStakingService(contract, celTokenContract);
    }
    return this.services.projectStaking;
  }

  /**
   * Get Emission Controller service
   * @returns {EmissionControllerService} - Emission Controller service instance
   */
  getEmissionControllerService() {
    if (!this.services.emissionController) {
      const contract = this.getEmissionControllerContract();
      this.services.emissionController = new EmissionControllerService(contract);
    }
    return this.services.emissionController;
  }

  /**
   * Get Protocol Treasury service
   * @returns {Object} - Protocol Treasury service
   */
  getProtocolTreasuryService() {
    console.log('getProtocolTreasuryService called');
    console.log('Existing services:', Object.keys(this.services));
    
    if (!this.services.protocolTreasury) {
      try {
        console.log('Creating new ProtocolTreasuryService');
        const contract = this.getProtocolTreasuryContract();
        console.log('Got contract with address:', contract.address);
        
        this.services.protocolTreasury = new ProtocolTreasuryService(contract, this.signer);
        console.log('Created ProtocolTreasuryService instance:', this.services.protocolTreasury);
        console.log('Methods available:', Object.getOwnPropertyNames(Object.getPrototypeOf(this.services.protocolTreasury)));
      } catch (error) {
        console.error('Error creating Protocol Treasury service:', error);
        throw error;
      }
    } else {
      console.log('Using existing ProtocolTreasuryService instance');
      console.log('Methods available:', Object.getOwnPropertyNames(Object.getPrototypeOf(this.services.protocolTreasury)));
    }
    
    return this.services.protocolTreasury;
  }

  /**
   * Get all services
   * @returns {Object} - Object containing all services
   */
  getAllServices() {
    return {
      celToken: this.getCELTokenService(),
      innovationUnits: this.getInnovationUnitsService(),
      projectStaking: this.getProjectStakingService(),
      emissionController: this.getEmissionControllerService(),
      protocolTreasury: this.getProtocolTreasuryService(),
      admin: new AdminService({
        celToken: this.getCELTokenService(),
        innovationUnits: {
          contract: this.getInnovationUnitsContract()
        },
        projectStaking: {
          contract: this.getProjectStakingContract()
        },
        emissionController: {
          contract: this.getEmissionControllerContract()
        },
        protocolTreasury: this.getProtocolTreasuryService()
      })
    };
  }

  /**
   * Reset all services and contracts
   * Called when signer changes (e.g. when user connects wallet)
   */
  resetServices(newSigner = null) {
  try {
    this.contracts = {};
    this.services = {};
    this.signer = newSigner || this.provider;
  } catch (error) {
    console.error('Error resetting services:', error);
    this.signer = this.provider;
  }
}


  /**
   * Change network
   * @param {string} networkId - Network ID
   */
  changeNetwork(networkId) {
    this.networkId = networkId.toLowerCase().replace(/_/g, '');
    this.config = null;
    
    // Try different possible formats
    if (config[this.networkId]) {
      this.config = config[this.networkId];
    } else if (config['optimismSepolia'] && 
              (this.networkId === 'optimismsepolia' || this.networkId === 'optimism_sepolia')) {
      this.config = config['optimismSepolia'];
      this.networkId = 'optimismSepolia'; // Normalize to the format in config
    } else if (config['optimism'] && 
              (this.networkId === 'optimism')) {
      this.config = config['optimism'];
    } else if (config['localhost'] && 
              (this.networkId === 'localhost')) {
      this.config = config['localhost'];
    }
    
    if (!this.config) {
      console.error(`No configuration found for network ${networkId}. Available networks: ${Object.keys(config).join(', ')}`);
      throw new Error(`No configuration found for network ${networkId}`);
    }
    
    this.resetServices();
  }
}

export default ContractServiceFactory; 